/**
 * RichTextEditor.jsx
 *
 * Lightweight WYSIWYG for comments: Bold, bullet & numbered lists, and a small
 * brand-book colour palette. Emits HTML via onChange; render it through
 * sanitizeRich() (richText.js) before displaying.
 *
 * Uncontrolled contentEditable — we only write to the DOM when `value` changes
 * externally (e.g. reset to '' after posting), so typing never loses the caret.
 */
import React, { useRef, useEffect, useState } from 'react';
import { sanitizePaste } from '../lib/richText.js';

const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Brand-book palette for coloured text (design tokens).
const COLORS = [
  { hex: '#1F2937', label: 'Default' },
  { hex: '#0D9488', label: 'Brand teal' },
  { hex: '#2563EB', label: 'Blue' },
  { hex: '#D97706', label: 'Amber' },
  { hex: '#DC2626', label: 'Red' },
];

const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

export default function RichTextEditor({ value, onChange, placeholder, rightSlot }) {
  const ref = useRef(null);
  const [colorOpen, setColorOpen] = useState(false);
  const colorRef = useRef(null);
  // On narrow widths the formatting buttons collapse into a single "format"
  // menu so the toolbar stays one row and the rightSlot (Public/Private) fits.
  const [formatOpen, setFormatOpen] = useState(false);
  const formatRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches
  );

  useEffect(() => {
    const el = ref.current;
    if (el && (value || '') !== el.innerHTML) el.innerHTML = value || '';
  }, [value]);

  useEffect(() => {
    if (!colorOpen) return;
    function onDown(e) { if (colorRef.current && !colorRef.current.contains(e.target)) setColorOpen(false); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [colorOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 700px)');
    const onChange = e => setIsNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!formatOpen) return;
    function onDown(e) { if (formatRef.current && !formatRef.current.contains(e.target)) setFormatOpen(false); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [formatOpen]);

  const emit = () => onChange(ref.current?.innerHTML || '');
  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };
  const addLink = () => {
    const url = window.prompt('Link URL (https://…)');
    if (url && url.trim()) exec('createLink', url.trim());
  };
  // Clean pasted content: keep bold/italic/lists/links, strip the styling spans,
  // inline styles and &nbsp; that Word/Docs/Slack/email inject. Plain text keeps
  // its line breaks. Prevents the "<span style='white-space:pre-wrap'>"/&nbsp;
  // junk from ever entering a comment.
  const onPaste = (e) => {
    e.preventDefault();
    const cb   = e.clipboardData || window.clipboardData;
    const html = cb?.getData('text/html');
    const text = cb?.getData('text/plain') || '';
    const toInsert = (html && html.trim())
      ? sanitizePaste(html)
      : escapeHtml(text).replace(/\r\n|\r|\n/g, '<br>');
    ref.current?.focus();
    document.execCommand('insertHTML', false, toInsert);
    emit();
  };

  const Tool = ({ title, onClick, children }) => (
    <button type="button" className="rich-tool-btn" title={title} aria-label={title}
      onMouseDown={e => e.preventDefault()} onClick={onClick}>
      {children}
    </button>
  );

  const tools = (
    <>
      <Tool title="Bold" onClick={() => exec('bold')}>
        <Icon><path d="M6 4h7a4 4 0 0 1 0 8H6z"/><path d="M6 12h9a4 4 0 0 1 0 8H6z"/></Icon>
      </Tool>
      <Tool title="Italic" onClick={() => exec('italic')}>
        <Icon><line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/><line x1="15" y1="4" x2="9" y2="20"/></Icon>
      </Tool>
      <Tool title="Underline" onClick={() => exec('underline')}>
        <Icon><path d="M6 4v6a6 6 0 0 0 12 0V4"/><line x1="4" y1="20" x2="20" y2="20"/></Icon>
      </Tool>
      <Tool title="Strikethrough" onClick={() => exec('strikeThrough')}>
        <Icon><path d="M16 4H9a3 3 0 0 0-2.83 4"/><path d="M14 12a4 4 0 0 1 0 8H6"/><line x1="4" y1="12" x2="20" y2="12"/></Icon>
      </Tool>
      <span className="rich-divider" />
      <Tool title="Bullet list" onClick={() => exec('insertUnorderedList')}>
        <Icon><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></Icon>
      </Tool>
      <Tool title="Numbered list" onClick={() => exec('insertOrderedList')}>
        <Icon><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/></Icon>
      </Tool>
      <span className="rich-divider" />
      <Tool title="Add link" onClick={addLink}>
        <Icon><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></Icon>
      </Tool>
      <Tool title="Clear formatting" onClick={() => { exec('removeFormat'); exec('unlink'); }}>
        <Icon><path d="M4 7V4h16v3"/><path d="M5 20h6"/><path d="M13 4 8 20"/><line x1="15" y1="15" x2="20" y2="20"/><line x1="20" y1="15" x2="15" y2="20"/></Icon>
      </Tool>
      <span className="rich-divider" />
      <div ref={colorRef} style={{ position: 'relative', display: 'inline-flex' }}>
        <button
          type="button"
          className="rich-tool-btn"
          title="Text colour"
          aria-label="Text colour"
          aria-expanded={colorOpen}
          onMouseDown={e => e.preventDefault()}
          onClick={() => setColorOpen(o => !o)}
        >
          <Icon>
            <path d="m6 16 6-12 6 12"/><path d="M8 12h8"/>
            <rect x="4" y="19" width="16" height="2.6" rx="1" fill="currentColor" stroke="none"/>
          </Icon>
        </button>
        {colorOpen && (
          <div className="rich-color-pop">
            {COLORS.map(c => (
              <button
                key={c.hex}
                type="button"
                className="rich-swatch"
                title={c.label}
                aria-label={`Text colour: ${c.label}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => { exec('foreColor', c.hex); setColorOpen(false); }}
                style={{ background: c.hex }}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="rich-editor-wrap">
      <div className="rich-toolbar">
        {isNarrow ? (
          <div className="rich-format-wrap" ref={formatRef}>
            <button
              type="button"
              className={`rich-tool-btn${formatOpen ? ' active' : ''}`}
              title="Formatting"
              aria-label="Formatting"
              aria-expanded={formatOpen}
              onMouseDown={e => e.preventDefault()}
              onClick={() => setFormatOpen(o => !o)}
            >
              <Icon><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></Icon>
            </button>
            {formatOpen && <div className="rich-tools-pop">{tools}</div>}
          </div>
        ) : tools}
        {rightSlot && <div className="rich-toolbar-right">{rightSlot}</div>}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onPaste={onPaste}
        data-placeholder={placeholder || ''}
        className="rich-editor"
      />
    </div>
  );
}
